import { useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { useIsFetching, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { apiCache } from '@/lib/cacheCleanup';

// "Slow internet / page reload" glyph (monitor frame with a reload circle
// inside). Inlined so we don't pull a new icon dependency. The path is split
// into two: the frame + browser dots stay static, the reload circle rotates
// while a page-wide refetch is in flight.
const PageReloadIcon = ({
  size = 16,
  isSpinning = false,
}: {
  size?: number;
  isSpinning?: boolean;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 122.88 108.99"
    fill="currentColor"
    aria-hidden="true"
  >
    {/* Frame + 3 browser-tab dots (static) */}
    <path
      fillRule="evenodd"
      d="M8.27,0H114.6a8.3,8.3,0,0,1,8.28,8.27V90.89a8.31,8.31,0,0,1-8.28,8.27h-8.77a49.46,49.46,0,0,0,3.14-7.8h4.71a2.34,2.34,0,0,0,1.62-.67,2.25,2.25,0,0,0,.67-1.62V20.6H7.23V89.07a2.29,2.29,0,0,0,.67,1.62,2.22,2.22,0,0,0,1.62.67h4.39a49.46,49.46,0,0,0,3.14,7.8H8.27A8.31,8.31,0,0,1,0,90.89V8.27A8.29,8.29,0,0,1,8.27,0ZM106.72,6.7a4.09,4.09,0,1,1-4.08,4.09,4.09,4.09,0,0,1,4.08-4.09ZM79,6.7a4.09,4.09,0,1,1-4.09,4.09A4.09,4.09,0,0,1,79,6.7ZM92.86,6.7a4.09,4.09,0,1,1-4.09,4.09A4.09,4.09,0,0,1,92.86,6.7Z"
    />
    {/* Reload circle (rotates while loading). `transform-box: fill-box` makes
        `transform-origin: center` resolve to the group's bounding-box centre,
        which is approximately the geometric centre of the arc — otherwise the
        rotation would pivot around (0,0) and orbit out of frame. */}
    <g
      style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      className={cn(isSpinning && 'animate-spin')}
    >
      <path
        fillRule="evenodd"
        d="M34.94,57l.19-.28-4.82.68A2.86,2.86,0,0,1,27.09,55a2.86,2.86,0,0,1,2.44-3.23L42,50h0l.23,0h0a2.83,2.83,0,0,1,2.92,2.74q.26,6.44.47,12.87a2.85,2.85,0,0,1-2.74,2.94h0A2.85,2.85,0,0,1,40,65.76c-.07-1.9-.13-3.8-.2-5.69-.41.59-.82,1.24-1.24,2A28.1,28.1,0,0,0,35,78.18a27.24,27.24,0,0,0,2,8,26.69,26.69,0,0,0,4.24,7h0a29.77,29.77,0,0,0,2.08,2.24c.72.71,1.48,1.38,2.26,2a28.23,28.23,0,0,0,5.5,3.48,26.8,26.8,0,0,0,6,2.09l.68.15v5.8l-1-.19a32.68,32.68,0,0,1-7.76-2.5,34.57,34.57,0,0,1-7-4.37c-.91-.74-1.8-1.53-2.66-2.37s-1.66-1.72-2.46-2.67a32.47,32.47,0,0,1-7.53-18.16,33.71,33.71,0,0,1,4.21-19.42c.46-.82.92-1.56,1.4-2.26ZM75.32,106.74a32.45,32.45,0,0,1-7,2.09l-1,.17V103.2l.69-.14a26.49,26.49,0,0,0,5.16-1.6,27.82,27.82,0,0,0,4.8-2.63l.59-.4,4.08,4.09-.82.59a33.78,33.78,0,0,1-6.46,3.62ZM93.57,88.56A32.78,32.78,0,0,1,89.93,95l-.59.82-4.09-4.09.41-.59a26.49,26.49,0,0,0,4.24-10l.14-.68h5.78l-.17,1a32.66,32.66,0,0,1-2.08,7.08ZM94.9,67.13c.26.94.48,1.89.65,2.83l.19,1H90l-.15-.67c-.14-.68-.31-1.35-.5-2s-.41-1.31-.65-2a32.5,32.5,0,0,0-1.36-3.15,31.8,31.8,0,0,0-1.64-2.92l-.37-.58,4.1-4.1.58.86a38.09,38.09,0,0,1,2.23,3.79A39.62,39.62,0,0,1,94,64.35c.35.93.65,1.85.91,2.78ZM77,45.28a29.22,29.22,0,0,1,5,2.88l.82.58L78.7,52.83l-.58-.39a23.75,23.75,0,0,0-3.8-2.1,24.77,24.77,0,0,0-4.13-1.4c-.65-.16-1.33-.29-2-.4s-1.37-.18-2.05-.22-1.43-.06-2.14,0a20.85,20.85,0,0,0-2.18.15h0a2.86,2.86,0,0,1-2.54-4.64,2.8,2.8,0,0,1,1.92-1c.89-.1,1.78-.16,2.68-.18s1.76,0,2.61,0,1.71.15,2.53.27,1.69.3,2.55.51A29.56,29.56,0,0,1,77,45.28Z"
      />
    </g>
  </svg>
);

interface ReloadButtonProps {
  /**
   * Query keys this button should invalidate. Pass the same key (or key
   * prefix) the section's `useQuery` uses. Pass an empty array (or omit)
   * for a global reload that touches every active query on the page.
   */
  queryKeys?: QueryKey[];
  /**
   * apiCache endpoint prefixes to clear before the React Query refetch.
   * Without this the in-memory apiCache (lib/api.ts) would short-circuit
   * the refetch and return stale data. Pass the same path prefix the
   * section's `fetcher()` uses (e.g. `/dashboard`, `/learning-bookings`).
   * Omit to clear the entire apiCache — used by the global navbar reload
   * where "the current page" can span many endpoints.
   */
  endpoints?: (string | RegExp)[];
  /**
   * Callback fired after apiCache is cleared. Use this on pages that don't
   * use React Query (raw `useState + useEffect + api.get` pattern) to
   * trigger the local refetch. The button awaits the returned promise so
   * the spinner reflects the in-flight state when `isFetching` isn't passed
   * explicitly.
   */
  onReload?: () => void | Promise<void>;
  /**
   * Force the spinner on. Use for `onReload`-driven pages where the loading
   * state lives in component state, not React Query (e.g. `isLoading` from
   * a local fetch).
   */
  isFetching?: boolean;
  /** Optional className for layout/positioning */
  className?: string;
  /** Optional title (tooltip). Defaults to "Reload". */
  title?: string;
  /** Visual size in pixels for the icon (button auto-sizes). */
  size?: number;
  /** Override label for screen readers. */
  ariaLabel?: string;
  /**
   * Icon variant. `section` (default) uses RefreshCcw for per-card reloads;
   * `page` uses the PageReloadIcon (monitor + reload arc) for the global
   * navbar reload so the two read as distinct actions at a glance.
   */
  variant?: 'section' | 'page';
}

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Reload button that hooks into React Query. Shows a spinning icon while
 * any of its target keys are fetching. When `queryKeys` is empty, it
 * invalidates all active queries on the current page (used by the navbar).
 */
export const ReloadButton = ({
  queryKeys,
  endpoints,
  onReload,
  isFetching: externalIsFetching,
  className,
  title = 'Reload',
  size = 16,
  ariaLabel,
  variant = 'section',
}: ReloadButtonProps) => {
  const queryClient = useQueryClient();
  const [callbackInFlight, setCallbackInFlight] = useState(false);

  // useIsFetching takes a single filter; for the multi-key case we sum
  // across all the supplied keys so the spinner reflects any in-flight
  // fetch among them.
  const fetchingTotal = useIsFetching(
    queryKeys && queryKeys.length > 0
      ? { predicate: (q) => queryKeys.some((k) => keyStartsWith(q.queryKey, k)) }
      : { type: 'active' },
  );
  const isSpinning =
    fetchingTotal > 0 || externalIsFetching === true || callbackInFlight;

  const handleClick = async () => {
    // The custom `apiCache` in lib/api.ts sits in front of React Query — if we
    // only invalidate the React Query cache, the resulting refetch will short-
    // circuit at apiCache and return the same stale data. A user-initiated
    // reload should always hit the network, so flush the relevant apiCache
    // entries first.
    if (endpoints && endpoints.length > 0) {
      // Section-scoped: only drop the endpoints this card depends on. Keeps
      // other cached endpoints warm so unrelated sections don't refetch on
      // their next render.
      for (const endpoint of endpoints) {
        const pattern =
          endpoint instanceof RegExp
            ? endpoint
            : new RegExp(`^${escapeRegex(endpoint)}`);
        apiCache.clearByPattern(pattern);
      }
    } else {
      // Page-scoped (no endpoint hints): flush everything. The next fetcher
      // call for each mounted query repopulates apiCache from the network.
      apiCache.clear();
    }

    if (queryKeys && queryKeys.length > 0) {
      for (const key of queryKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    } else if (!onReload) {
      // No explicit keys and no callback: assume the caller wants a global
      // refetch of every active React Query on the page.
      void queryClient.invalidateQueries({ type: 'active' });
    }

    if (onReload) {
      try {
        setCallbackInFlight(true);
        await onReload();
      } finally {
        setCallbackInFlight(false);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={title}
      aria-label={ariaLabel ?? title}
      disabled={isSpinning}
      className={cn(
        'inline-flex items-center justify-center rounded-full p-1.5 text-[#9FA2B4] transition-colors',
        'hover:bg-[#F4F0FF] hover:text-[#3C3295] cursor-pointer',
        'disabled:cursor-progress disabled:opacity-60',
        className,
      )}
    >
      {variant === 'page' ? (
        // For the page variant, the spin lives inside the SVG — only the
        // reload circle rotates while the monitor frame stays put.
        <PageReloadIcon size={size} isSpinning={isSpinning} />
      ) : (
        <span
          className={cn(
            'inline-flex transition-transform',
            isSpinning && 'animate-spin',
          )}
        >
          <RefreshCcw size={size} />
        </span>
      )}
    </button>
  );
};

// Match `target` as a prefix of `key`. React Query's invalidate already does
// prefix matching, but `useIsFetching` with a predicate needs this manually.
function keyStartsWith(key: QueryKey, target: QueryKey): boolean {
  if (!Array.isArray(target) || target.length === 0) return true;
  if (!Array.isArray(key)) return false;
  if (key.length < target.length) return false;
  for (let i = 0; i < target.length; i++) {
    if (JSON.stringify(key[i]) !== JSON.stringify(target[i])) return false;
  }
  return true;
}
